import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

export const EDITIONS = ["2019", "2022", "2025"];
const KEY = "fab_nfpa_edition";
const EMPTY = { quiz: {}, guide: {} };
const Ctx = createContext({ edition: "2025", setEdition: () => {}, variants: EMPTY });

// Site-wide NFPA 72 edition. Approved AI variants for the edition are fetched once and applied by quiz/guides.
export function EditionProvider({ children }) {
  const [edition, setEditionState] = useState(() => (EDITIONS.includes(localStorage.getItem(KEY)) ? localStorage.getItem(KEY) : "2025"));
  const [variants, setVariants] = useState(EMPTY);
  useEffect(() => {
    let on = true;
    api.get(`/editions/${edition}`).then(({ data }) => { if (on) setVariants({ quiz: data.quiz || {}, guide: data.guide || {} }); }).catch(() => { if (on) setVariants(EMPTY); });
    return () => { on = false; };
  }, [edition]);
  const setEdition = (e) => { if (EDITIONS.includes(e)) { localStorage.setItem(KEY, e); setEditionState(e); } };
  const value = useMemo(() => ({ edition, setEdition, variants }), [edition, variants]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useEdition = () => useContext(Ctx);

// Swap a quiz question for its approved edition variant (same id), keeping the bank shape.
export function applyQuizEdition(pool, variants) {
  const v = variants?.quiz || {};
  return pool.map((q) => {
    const c = v[q.id];
    if (!c || !Array.isArray(c.options) || c.options.length !== q.options.length) return q;
    return { ...q, q: c.q || q.q, options: c.options, answer: Number.isInteger(c.answer) ? c.answer : q.answer, explanation: c.explanation || q.explanation, editionNote: c.edition_note || null };
  });
}

export function EditionToggle({ compact = false, className = "" }) {
  const { edition, setEdition } = useEdition();
  return (
    <div role="radiogroup" aria-label="NFPA 72 edition" data-testid="edition-toggle" className={`inline-flex items-center rounded-full border border-white/15 bg-secondary/60 p-0.5 ${className}`}>
      {!compact && <span className="pl-2.5 pr-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">NFPA 72</span>}
      {EDITIONS.map((e) => (
        <button key={e} type="button" role="radio" aria-checked={edition === e} data-testid={`edition-${e}`} onClick={() => setEdition(e)}
          className={`h-7 px-2.5 rounded-full text-xs font-bold transition-colors ${edition === e ? "bg-primary text-white" : "text-slate-300 hover:text-white"}`}>{e}</button>
      ))}
    </div>
  );
}
